import React, { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

/**
 * EngagementProcess — high-end editorial horizontal stepper.
 *
 * The four stages laid out left-to-right on desktop, connected by a thin
 * sage-emerald track. When the section enters view, the track DRAWS itself
 * from left → right; each step activates in sequence as the line passes
 * its position. Mobile degrades to a vertical stepper using the same idea.
 *
 * Content is preserved exactly from the previous version — this is a
 * visual + motion redesign only. No copy added, removed, or reworded.
 */

// ---------- Content (verbatim from previous version) ----------
const STAGES = [
  {
    label: 'Stage 01',
    title: 'Onboarding & Connections',
    duration: 'Week 1',
    desc: 'We connect to your POS, link Tally through a one-time accountant install, and gather your contracted supplier rate sheets. POS data starts flowing automatically — no daily exports needed from your operator.',
  },
  {
    label: 'Stage 02',
    title: 'Financial Diagnostic',
    duration: 'Weeks 1–3',
    desc: 'A first deep audit of the last 30–90 days: three-way reconciliation, supplier variance scan, settlement investigation, cost outlier detection. Output is a written findings memo with every recovery candidate quantified.',
  },
  {
    label: 'Stage 03',
    title: 'Active Recovery',
    duration: 'Weeks 3–12',
    desc: 'Disputes drafted and dispatched in your name — to aggregators, suppliers and banks. We follow up until the cash is back in your account. Concurrently we begin supplier renegotiations, menu economics review and any tailored work your business needs.',
  },
  {
    label: 'Stage 04',
    title: 'Continuous Reporting Cycle',
    duration: 'Ongoing',
    desc: 'Weekly Brief every Monday at 08:00. Monthly Detailed Report on the seventh. Quarterly business review with you. The engagement compounds — each month\'s benchmarks make the next month\'s analysis sharper.',
  },
];

const TAGS = [
  'Menu economics',
  'Supplier renegotiation',
  'Pre-investment diligence',
  'Pre-audit cleanup',
  'Board + investor reporting',
  'Fractional CFO support',
  'Special-situation financial work',
];

const ROMAN = ['i', 'ii', 'iii', 'iv'];

// ---------- Palette (matched to WhyEpicurea cream — re-tuned for light bg) ----------
const BG_TOP    = '#F4ECDB';                       // warm cream (top of gradient, same as WhyEpicurea)
const BG_BOT    = '#ECE2C7';                       // warm cream (bottom of gradient)
const INK       = '#28282B';                       // deep warm near-black for titles/headline
const MUTED     = '#5A4A20';                       // muted warm brown for body / descriptions
const MUTED_FAINT = 'rgba(40, 40, 43, 0.45)';      // small caps "Stage 01" etc.
const SAGE      = '#4D7D69';                       // DEEPER sage — real contrast on cream
const SAGE_SOFT = 'rgba(77, 125, 105, 0.55)';      // track / hairlines
const NUMERAL_INK = 'rgba(40, 40, 43, 0.42)';      // quiet ink for roman numerals (not faint bone)
const CTA_BG    = '#28282B';                       // dark pill inverts from previous bone pill
const CTA_TEXT  = '#F4ECDB';

// ---------- Timing ----------
const LINE_DURATION = 1.4;         // seconds for the track to fully draw
const LINE_EASE     = [0.16, 1, 0.3, 1]; // expo-out: accel then settle

// Each step activates as the line passes it — delay = position * line duration
const stepDelay = (i, n) => (i / (n - 1)) * LINE_DURATION;

const STEP_SPRING = { type: 'spring', stiffness: 85, damping: 18, mass: 0.8 };
const NODE_SPRING = { type: 'spring', stiffness: 160, damping: 16, mass: 0.5 };

// ============================================================
// StepCard — title / duration / desc
// ============================================================
const StepCard = ({ stage, index, inView, reduced, total }) => {
  const delay = reduced ? 0 : stepDelay(index, total) - 0.08; // start a hair before the line reaches the node
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 22 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 }}
      transition={reduced ? { duration: 0.5 } : { ...STEP_SPRING, delay }}
      className="relative"
      data-testid={`engagement-step-${index}`}
    >
      <div
        className="text-[10px] uppercase tracking-[0.30em] font-medium mb-3"
        style={{ color: MUTED_FAINT }}
      >
        {stage.label}
      </div>
      <h3
        className="text-xl md:text-[22px] lg:text-[24px] tracking-tight leading-[1.18] mb-2"
        style={{
          color: INK,
          fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
          fontWeight: 500,
        }}
      >
        {stage.title}
      </h3>
      <div
        className="text-[10px] uppercase tracking-[0.22em] mb-5"
        style={{ color: 'rgba(77, 125, 105, 0.92)' }}
      >
        {stage.duration}
      </div>
      <p
        className="text-[14px] md:text-[14.5px] leading-relaxed"
        style={{
          color: MUTED,
          fontFamily: "'Manrope', system-ui, sans-serif",
        }}
      >
        {stage.desc}
      </p>
    </motion.div>
  );
};

// ============================================================
// MarkerLabel — quiet roman numeral above each desktop node
// ============================================================
const MarkerLabel = ({ index, inView, reduced, total }) => {
  const delay = reduced ? 0 : stepDelay(index, total) - 0.05;
  return (
    <motion.div
      aria-hidden="true"
      initial={reduced ? false : { opacity: 0, y: 4 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 4 }}
      transition={reduced ? { duration: 0.4 } : { ...STEP_SPRING, delay }}
      className="absolute -top-7 left-0 leading-none select-none"
      style={{
        fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
        fontStyle: 'italic',
        fontWeight: 400,
        fontSize: '18px',
        color: NUMERAL_INK,
        letterSpacing: '0.02em',
      }}
    >
      {ROMAN[index]}
    </motion.div>
  );
};

// ============================================================
// Section
// ============================================================
export const EngagementProcess = () => {
  const reduced = useReducedMotion();
  const stepperRef = useRef(null);
  const inView = useInView(stepperRef, { once: true, amount: 0.22 });

  return (
    <section
      id="pricing"
      data-testid="landing-engagement"
      className="relative py-28 md:py-44 overflow-hidden"
      style={{ background: `linear-gradient(180deg, ${BG_TOP} 0%, ${BG_BOT} 100%)` }}
    >
      {/* TOP feather — blends down from the dark ImpactNumbers above */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-0 right-0 h-40 z-[1]"
        style={{ background: `linear-gradient(180deg, #0F0B0A 0%, ${BG_TOP} 100%)` }}
      />
      {/* BOTTOM feather — fades to dark for the FAQ section below */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-40 z-[1]"
        style={{ background: `linear-gradient(0deg, #0F0B0A 0%, ${BG_BOT} 100%)` }}
      />
      {/* Paper grain — same recipe as WhyEpicurea (multiply blend on warm cream) */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />
      {/* Asymmetric soft daylight — warm window from upper-left */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 65% 50% at 22% 18%, rgba(255, 244, 218, 0.45) 0%, rgba(255, 244, 218, 0.15) 35%, rgba(244, 236, 219, 0) 70%)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        {/* Editorial header (copy preserved verbatim) */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={reduced ? { duration: 0.5 } : { type: 'spring', stiffness: 78, damping: 19, mass: 0.9 }}
          className="max-w-3xl mb-20 md:mb-28"
        >
          <div
            className="text-[10px] md:text-xs uppercase tracking-[0.34em] font-medium mb-6 flex items-center gap-3"
            style={{ color: 'rgba(77, 125, 105, 0.85)' }}
          >
            <span className="block w-10 h-px" style={{ background: SAGE, opacity: 0.65 }} />
            Engagement Model
          </div>
          <h2
            className="tracking-tight leading-[1.05] text-4xl md:text-5xl lg:text-[60px]"
            style={{ color: INK, fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif", fontWeight: 500 }}
          >
            Tailored to each client.
            <span className="block italic" style={{ color: SAGE }}>
              Built around four stages.
            </span>
          </h2>
          <p
            className="text-base md:text-lg mt-7 leading-relaxed max-w-2xl"
            style={{ color: MUTED, fontFamily: "'Manrope', system-ui, sans-serif" }}
          >
            Every restaurant is different — some need a one-time clean-up before audit, some need an ongoing recovery partner, some need help with menu economics, supplier strategy or pre-investment diligence. Our scope is shaped around what you actually need. What stays consistent is the underlying process.
          </p>
        </motion.div>

        {/* Stepper container — single inView ref so both desktop and mobile activate */}
        <div ref={stepperRef}>

          {/* ============================================================
              DESKTOP — horizontal stepper
              ============================================================ */}
          <div className="hidden md:block relative">
          {/* Track region — line + nodes + numerals */}
          <div className="relative h-12 mb-12">
            {/* Faint base track (always visible so the column never looks empty) */}
            <div
              aria-hidden="true"
              className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px"
              style={{ background: SAGE, opacity: 0.22 }}
            />
            {/* Drawn track — animates scaleX 0 → 1 left-to-right */}
            <motion.div
              aria-hidden="true"
              initial={reduced ? false : { scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
              transition={reduced ? { duration: 0.4 } : { duration: LINE_DURATION, ease: LINE_EASE }}
              style={{
                background: SAGE,
                opacity: 0.85,
                transformOrigin: 'left center',
                boxShadow: '0 0 6px rgba(77, 125, 105, 0.30)',
              }}
              className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px"
            />

            {/* Nodes — distributed evenly across the track. They pop in as the line passes each. */}
            {STAGES.map((_, i) => {
              // First node at 0%, last at 100%, middles evenly between.
              const leftPct = (i / (STAGES.length - 1)) * 100;
              const delay = reduced ? 0 : stepDelay(i, STAGES.length) - 0.04;
              return (
                <div
                  key={i}
                  className="absolute top-1/2 -translate-y-1/2"
                  style={{ left: `${leftPct}%`, transform: `translate(-50%, -50%)` }}
                >
                  {/* Node dot */}
                  <motion.div
                    initial={reduced ? false : { scale: 0, opacity: 0 }}
                    animate={inView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                    transition={reduced ? { duration: 0.3 } : { ...NODE_SPRING, delay }}
                    className="relative w-2.5 h-2.5 rounded-full"
                    style={{
                      background: SAGE,
                      boxShadow: '0 0 12px rgba(77, 125, 105, 0.45), 0 0 0 4px #F0E7D2',
                    }}
                    data-testid={`engagement-node-${i}`}
                  />
                  {/* Roman numeral above the node */}
                  <MarkerLabel index={i} inView={inView} reduced={!!reduced} total={STAGES.length} />
                </div>
              );
            })}
          </div>

          {/* Step content — 4 columns aligned to the nodes */}
          <div className="grid grid-cols-4 gap-x-8 lg:gap-x-12">
            {STAGES.map((stage, i) => (
              <StepCard
                key={stage.title}
                stage={stage}
                index={i}
                inView={inView}
                reduced={!!reduced}
                total={STAGES.length}
              />
            ))}
          </div>
        </div>

        {/* ============================================================
            MOBILE — vertical stepper
            ============================================================ */}
        <div className="md:hidden relative">
          {/* Faint base track */}
          <div
            aria-hidden="true"
            className="absolute left-[7px] top-2 bottom-2 w-px"
            style={{ background: SAGE, opacity: 0.22 }}
          />
          {/* Drawn track — animates scaleY 0 → 1 top-to-bottom */}
          <motion.div
            aria-hidden="true"
            initial={reduced ? false : { scaleY: 0 }}
            animate={inView ? { scaleY: 1 } : { scaleY: 0 }}
            transition={reduced ? { duration: 0.4 } : { duration: LINE_DURATION, ease: LINE_EASE }}
            style={{
              background: SAGE,
              opacity: 0.85,
              transformOrigin: 'top center',
              boxShadow: '0 0 6px rgba(77, 125, 105, 0.30)',
            }}
            className="absolute left-[7px] top-2 bottom-2 w-px"
          />

          <div className="space-y-12">
            {STAGES.map((stage, i) => {
              const delay = reduced ? 0 : stepDelay(i, STAGES.length) - 0.04;
              return (
                <div key={stage.title} className="relative pl-9">
                  {/* Mobile node + roman numeral */}
                  <motion.div
                    initial={reduced ? false : { scale: 0, opacity: 0 }}
                    animate={inView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                    transition={reduced ? { duration: 0.3 } : { ...NODE_SPRING, delay }}
                    className="absolute left-[2.5px] top-1.5 w-2.5 h-2.5 rounded-full"
                    style={{
                      background: SAGE,
                      boxShadow: '0 0 10px rgba(77, 125, 105, 0.45), 0 0 0 3px #F0E7D2',
                    }}
                  />
                  <div
                    aria-hidden="true"
                    className="absolute left-[18px] top-[-22px] leading-none select-none"
                    style={{
                      fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
                      fontStyle: 'italic',
                      fontWeight: 400,
                      fontSize: '15px',
                      color: NUMERAL_INK,
                    }}
                  >
                    {ROMAN[i]}
                  </div>
                  <StepCard
                    stage={stage}
                    index={i}
                    inView={inView}
                    reduced={!!reduced}
                    total={STAGES.length}
                  />
                </div>
              );
            })}
          </div>
        </div>
        {/* end stepper container */}
        </div>

        {/* ============================================================
            Beyond-reconciliation note + CTA (copy preserved verbatim)
            ============================================================ */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={reduced ? { duration: 0.5 } : { type: 'spring', stiffness: 78, damping: 21, mass: 0.85 }}
          className="mt-20 md:mt-28 pt-10 md:pt-12 border-t flex flex-col md:flex-row md:items-center gap-6 md:gap-10"
          style={{ borderColor: 'rgba(40, 40, 43, 0.12)' }}
        >
          <div className="flex-1">
            <div
              className="text-lg md:text-xl tracking-tight mb-4"
              style={{
                color: INK,
                fontFamily: "'Fraunces', 'Playfair Display', Georgia, serif",
                fontWeight: 500,
              }}
            >
              Beyond reconciliation — we also help with:
            </div>
            <div className="flex flex-wrap gap-2">
              {TAGS.map((tag) => (
                <span
                  key={tag}
                  className="text-[11px] uppercase tracking-[0.18em] rounded-full px-3 py-1.5 border"
                  style={{
                    color: MUTED,
                    borderColor: 'rgba(40, 40, 43, 0.18)',
                    fontFamily: "'Manrope', system-ui, sans-serif",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <a
            href="#contact"
            data-testid="engagement-cta"
            className="group flex-shrink-0 inline-flex items-center justify-center gap-2 rounded-full font-semibold px-6 py-3 text-sm transition-all duration-300"
            style={{ backgroundColor: CTA_BG, color: CTA_TEXT }}
          >
            Talk to us about your scope
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default EngagementProcess;
